<script lang="ts">
  import { t } from '$lib/i18n';
  import { customSlide } from '$lib/transitions';
  import { navbarInfo } from '$components/navigation/navbar.svelte';
  import { CART_DRAWER_ID } from '$lib/constants';
  import { drawer } from '$lib/stores/drawer';
  import Icon from '$components/ui/icon/icon.svelte';
  import ShopifyImg from '$components/shopify-img.svelte';
  import { splitTitleAndCategory } from '$lib/utils/shopify/splitTitleAndCategory';
  import { onMount } from 'svelte';
  import { lastAddedItemId } from '$lib/cart';
  import {
    AnalyticsEvents,
    KlavyioAnalyticsEvents,
    analyticsEvent,
    klavyioAnalyticsEvent
  } from '$lib/analytics';
  import { cart } from '$lib/cart';
  import { nostoSession, sendNostoEvent } from '$lib/nosto';
  import { region } from '$lib/region';
  import { lastAddedItemLocation } from '$lib/cart/cart';
  import { page } from '$app/stores';
  import { abTestVersion } from '$lib/stores/ab-test-version';

  $: item = $cart?.lines.nodes.find((item) => item.merchandise.id === $lastAddedItemId);

  onMount(() => {
    if (item) {
      const variantTitle = item.merchandise.selectedOptions
        .map((option) => option.value)
        .join(' / ');

      analyticsEvent({
        event: AnalyticsEvents.ADD_TO_CART,
        currency: item.cost.amountPerQuantity.currencyCode,
        value: +item.cost.amountPerQuantity.amount,
        item_list_name: $lastAddedItemLocation,
        items: [
          {
            item_id: item.merchandise.product.handle,
            item_name: item.merchandise.product.title,
            item_category: item.merchandise.product.productType,
            item_variant: variantTitle,
            item_sku: item.merchandise.sku
          }
        ],
        test_group: $abTestVersion
      });

      if ($cart) {
        klavyioAnalyticsEvent({
          event: KlavyioAnalyticsEvents.ADDED_TO_CART,
          cart: {
            $value: $cart.cost.totalAmount.amount,
            total_price: $cart.cost.totalAmount.amount,
            original_total_price: $cart.cost.subtotalAmount.amount,
            items: $cart.lines.nodes
          }
        });

        lastAddedItemLocation.reset();
      }
    }
  });

  $: if (item && $cart && $cart.lines.nodes.length >= 1) {
    sendNostoEvent({
      type: 'sendCart',
      nostoSession: $nostoSession || '',
      cart: $cart,
      target: `${window.location.href}/cart`,
      region: $region
    });
    sendNostoEvent({
      type: 'updateCart',
      nostoSession: $nostoSession || '',
      target: `${window.location.href}/cart`,
      region: $region
    });
  }
</script>

{#if item && $drawer.id !== CART_DRAWER_ID}
  <button
    name="add-to-cart"
    aria-label="Cart Toast"
    on:click={() => {
      drawer.open(CART_DRAWER_ID);
    }}
    transition:customSlide|global={{ from: -10 }}
    style="--cart-notification-top: calc({($navbarInfo?.rect.top || 0) +
      ($navbarInfo?.rect.height || 0)}px)"
    class="shadow-sm fixed left-4 w-[calc(100%-theme(space.4)*2)] right-4 top-[--cart-notification-top] rounded-sm bg-highlight lg:absolute lg:left-auto lg:right-0 lg:w-96 lg:top-8"
  >
    <div class="border-b border-primary/4 p-2.5 text-accent">
      <span class="flex items-center justify-center gap-1 text-sm font-medium">
        <Icon size="md" icon="check-circle" />
        {$t('cart.added')}
      </span>
    </div>
    <div class="flex items-start justify-between p-2.5 text-left text-sm">
      <div class="flex items-start gap-2.5">
        {#if item}
          {@const { title, category } = splitTitleAndCategory({
            title: item.merchandise.product.title,
            titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
          })}
          <ShopifyImg
            class="h-11 w-11 rounded-xs bg-primary/2"
            src={item.merchandise.image?.url}
            alt={item.merchandise.image?.altText || ''}
            widths={[44]}
            sizes="44px"
          />
          <div>
            <p class="text-sm font-semibold">{title}</p>
            <p class="text-sm text-primary/40">{category}</p>
          </div>
        {/if}
      </div>
    </div>
  </button>
{/if}
